import { useState } from "react";
import toast from "react-hot-toast";
import { Check, Copy, Eye, EyeOff, KeyRound, X } from "lucide-react";
import { Modal } from "@veorest/ui";
import { useI18n } from "../../i18n";
import type { AccountCredentials } from "../../lib/partnerAccounts";

interface CredentialsModalProps {
  open: boolean;
  title: string;
  subtitle: string;
  credentials: AccountCredentials | null;
  onClose: () => void;
}

function CopyRow({
  label,
  value,
  masked,
  onToggleMask,
}: {
  label: string;
  value: string;
  masked?: boolean;
  onToggleMask?: () => void;
}) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);

  const copy = () => {
    void navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1600);
        toast.success(t("common.copied"));
      })
      .catch(() => toast.error(t("common.copyFailed")));
  };

  return (
    <div className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] px-4 py-3">
      <p className="text-[11px] font-black uppercase tracking-wider text-[var(--text-muted)]">
        {label}
      </p>
      <div className="mt-1.5 flex items-center gap-2">
        <p
          className="min-w-0 flex-1 truncate font-mono text-lg font-black text-[var(--text-primary)]"
          dir="ltr"
        >
          {masked ? "•".repeat(Math.max(8, value.length)) : value}
        </p>
        {onToggleMask ? (
          <button
            type="button"
            onClick={onToggleMask}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[var(--border-subtle)] text-[var(--text-secondary)]"
            aria-label={masked ? t("accounts.show") : t("accounts.hide")}
          >
            {masked ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
          </button>
        ) : null}
        <button
          type="button"
          onClick={copy}
          className="inline-flex h-9 items-center gap-1.5 rounded-xl border border-[var(--border-subtle)] px-3 text-xs font-bold text-[var(--accent)]"
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          {t("common.copy")}
        </button>
      </div>
    </div>
  );
}

/**
 * Shows the username + initial password exactly once (creation or reset).
 * Nothing here is persisted — closing the modal discards the password.
 */
export function CredentialsModal({
  open,
  title,
  subtitle,
  credentials,
  onClose,
}: CredentialsModalProps) {
  const { t } = useI18n();
  const [masked, setMasked] = useState(true);

  if (!open || !credentials) return null;

  return (
    <Modal open={open} onClose={onClose} panelClassName="max-w-md">
      <div className="flex max-h-[calc(100vh-32px)] flex-col overflow-y-auto">
        <div className="flex items-start justify-between border-b border-[var(--border-subtle)] px-5 py-4">
          <div className="min-w-0">
            <p className="text-[11px] font-black uppercase tracking-wider text-[var(--text-muted)]">
              {t("accounts.eyebrow")}
            </p>
            <h2 className="mt-1 truncate text-lg font-extrabold text-[var(--text-primary)]">
              {title}
            </h2>
          </div>
          <button
            type="button"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[var(--border-subtle)]"
            onClick={onClose}
            aria-label={t("common.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3 px-5 py-4">
          <div className="flex items-start gap-2 rounded-xl border border-[var(--accent)]/30 bg-[var(--accent-light)] px-3 py-2.5">
            <KeyRound className="mt-0.5 h-4 w-4 shrink-0 text-[var(--accent)]" />
            <p className="text-xs font-semibold leading-5 text-[var(--text-secondary)]">
              {t("accounts.shownOnce")}
            </p>
          </div>

          <CopyRow label={t("accounts.username")} value={credentials.username ?? "—"} />
          <CopyRow
            label={t("accounts.password")}
            value={credentials.password}
            masked={masked}
            onToggleMask={() => setMasked((m) => !m)}
          />

          <p className="pt-1 text-xs leading-5 text-[var(--text-muted)]">{subtitle}</p>
        </div>

        <div className="border-t border-[var(--border-subtle)] px-5 py-4">
          <button type="button" className="btn-primary w-full" onClick={onClose}>
            {t("accounts.done")}
          </button>
        </div>
      </div>
    </Modal>
  );
}
